import React, { useState, useEffect, useRef } from 'react';
import { Container, Button, Spinner, Card } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle, faApple } from '@fortawesome/free-brands-svg-icons';
import { SparkleButton } from '../../components/SparkleEffect';
import SessionGroupCard from './SessionGroupCard';
import PastSessionGroups from './PastSessionGroups';
import './WeeklyWorkshop.css';
import Loading from '../../components/Loading';
import DOMPurify from 'dompurify';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import { useNavigate } from 'react-router-dom';
import useUser from '../../hooks/useUser';

const WeeklyWorkshop = () => {
  const { 
    isAuthenticated, 
    user, 
    loginWithRedirect,
    isLoading: authLoading,
    getApiToken 
  } = useUser();
  const navigate = useNavigate();
  
  const [courseData, setCourseData] = useState(null);
  const [sessionGroup, setSessionGroup] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [apiError, setApiError] = useState('');
  const [pastSessionGroups, setPastSessionGroups] = useState([]);
  const [futureSessionGroups, setFutureSessionGroups] = useState([]);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredCohorts, setRegisteredCohorts] = useState({});
  const [highlightedCalendarButtons, setHighlightedCalendarButtons] = useState(null);
  const [upcomingVisibleCount, setUpcomingVisibleCount] = useState(1);
  const isScrollingToSessionRef = useRef(false); // Prevent scroll conflicts

  useEffect(() => {
    if (sessionGroup && sessionGroup.id) {
      const storedRegistration = localStorage.getItem(`edu-workshop-registered-${sessionGroup.id}`);
      
      if (storedRegistration) {
        try {
          const registrationData = JSON.parse(storedRegistration);
          
          // CRITICAL FIX: For workshop, allow both authenticated users with matching ID and unauthenticated guest registrations
          if ((!isAuthenticated && !registrationData.userId) || 
              (isAuthenticated && registrationData.userId === user?.sub)) {
            setIsRegistered(true);
          } else if (isAuthenticated && registrationData.userId && registrationData.userId !== user?.sub) {
            // Clear registration data from a different user
            console.log(`[WeeklyWorkshop] Clearing session registration data from different user: ${registrationData.userId} vs ${user?.sub}`);
            localStorage.removeItem(`edu-workshop-registered-${sessionGroup.id}`);
            setIsRegistered(false);
          }
        } catch (err) {
          console.error("Error parsing stored registration data:", err);
          localStorage.removeItem(`edu-workshop-registered-${sessionGroup.id}`);
        }
      }
    }
  }, [sessionGroup, isAuthenticated, user]);

  useEffect(() => {
    let mounted = true;
    
    const fetchWorkshopInfo = async () => {
      setIsLoading(true);
      setServiceUnavailable(false);
      
      try {
        let headers = {};
        if (isAuthenticated) {
          try {
            const token = await getApiToken();
            headers = { 'Authorization': `Bearer ${token}` };
          } catch (tokenError) {
            console.error('[WeeklyWorkshop] Failed to get API token:', tokenError);
            // Continue without token - user might still see basic content
          }
        }
        
        const courseId = process.env.REACT_APP_WEEKLY_WORKSHOP_COURSE_ID;
        if (!courseId) {
          throw new Error('Weekly workshop course ID not configured');
        }
        
        console.log('Fetching course data for ID:', courseId);
        
        const courseResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/courses/${courseId}`, 
          { headers }
        );
        
        if (!courseResponse.ok) {
          throw new Error(`Failed to fetch course: ${courseResponse.status}`);
        }
        
        const courseDataFromApi = await courseResponse.json();
        console.log('Course data received:', courseDataFromApi);
        
        if (mounted) {
          setCourseData(courseDataFromApi);
          
          try {
            console.log('Fetching all session groups...');
            const sessionGroupsResponse = await fetch(
              `${process.env.REACT_APP_API_URL}/courses/session-groups?courseId=${courseId}`,
              { headers }
            );
            
            if (!sessionGroupsResponse.ok) {
              throw new Error(`Failed to fetch session groups: ${sessionGroupsResponse.status}`);
            }
            
            const sessionGroupsData = await sessionGroupsResponse.json();
            console.log('All session groups data:', sessionGroupsData);
            
            const { sessionGroups } = sessionGroupsData;
            
            if (sessionGroups && sessionGroups.length > 0) {
              console.log('Found session groups:', sessionGroups.length);
              
              const isSessionGroupPast = (group) => {
                if (!group.sessions || group.sessions.length === 0) return false;
                
                const sortedSessions = [...group.sessions].sort((a, b) => 
                  new Date(b.scheduledAt) - new Date(a.scheduledAt)
                );
                
                const lastSession = sortedSessions[0];
                const lastSessionDate = new Date(lastSession.scheduledAt);
                
                // Calculate session end time using durationMinutes
                const durationMinutes = lastSession.durationMinutes || 60;
                const sessionEndTime = new Date(lastSessionDate.getTime() + durationMinutes * 60 * 1000);
                const now = new Date();
                
                console.log('Checking if group is past:', {
                  groupId: group.id,
                  lastSessionStart: lastSessionDate.toISOString(),
                  sessionEndTime: sessionEndTime.toISOString(),
                  now: now.toISOString(),
                  durationMinutes: durationMinutes,
                  isPast: now > sessionEndTime
                });
                
                // A session group is past if the last session has ended
                return now > sessionEndTime;
              };
              
              const allGroupDetails = await Promise.all(
                sessionGroups.map(async (group) => {
                  try {
                    console.log(`Fetching details for session group: ${group.id}`);
                    const response = await fetch(
                      `${process.env.REACT_APP_API_URL}/courses/session-groups/${group.id}`,
                      { headers }
                    );
                    
                    if (response.ok) {
                      return await response.json();
                    }
                  } catch (error) {
                    console.error(`Error fetching details for session group ${group.id}:`, error);
                  }
                  return null;
                })
              );
              
              const validGroupDetails = allGroupDetails.filter(Boolean);
              
              const pastGroups = [];
              const futureGroups = [];
              let currentGroup = null;
              
              validGroupDetails.forEach(group => {
                if (isSessionGroupPast(group)) {
                  pastGroups.push(group);
                } else {
                  if (!currentGroup) {
                    currentGroup = group;
                  } else if (group.nextSession && currentGroup.nextSession) {
                    const groupDate = new Date(group.nextSession.scheduledAt);
                    const currentDate = new Date(currentGroup.nextSession.scheduledAt);
                    
                    if (groupDate < currentDate) {
                      futureGroups.push(currentGroup);
                      currentGroup = group;
                    } else {
                      futureGroups.push(group);
                    }
                  } else {
                    if (group.nextSession && !currentGroup.nextSession) {
                      futureGroups.push(currentGroup);
                      currentGroup = group;
                    } else {
                      futureGroups.push(group);
                    }
                  }
                }
              });
              
              console.log('Categorized session groups:', {
                current: currentGroup ? 1 : 0,
                upcoming: futureGroups.length,
                past: pastGroups.length
              });
              
              setSessionGroup(currentGroup);
              setFutureSessionGroups(futureGroups);
              setPastSessionGroups(pastGroups);
              
              if (currentGroup && currentGroup.registration) {
                console.log('User is registered for this session group');
                setIsRegistered(true);
              }
            } else {
              console.log('No session groups found');
            }
          } catch (err) {
            console.error('Error fetching session groups:', err);
          }
          
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error fetching workshop data:', err);
        if (mounted) {
          setServiceUnavailable(true);
          setIsLoading(false);
        }
      }
    };
    
    fetchWorkshopInfo();
    
    return () => {
      mounted = false;
    };
  }, [isAuthenticated, getApiToken]);

  // CRITICAL FIX: Clear registration data when user changes for authenticated sessions
  // Workshop allows guest registrations, so we only clear authenticated user data that doesn't match
  useEffect(() => {
    if (isAuthenticated && user?.sub) {
      const currentUserId = user.sub;
      
      // Check all workshop registration data and clear any that belongs to different authenticated users
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('edu-workshop-registered-')) {
          try {
            const storedData = JSON.parse(localStorage.getItem(key));
            // Only clear if it's from a different authenticated user (preserve guest registrations)
            if (storedData.userId && storedData.userId !== currentUserId) {
              console.log(`[WeeklyWorkshop] Clearing registration data from previous authenticated user: ${key}`);
              localStorage.removeItem(key);
            }
          } catch {
            // If data is corrupted, remove it
            localStorage.removeItem(key);
          }
        }
      });
    }
  }, [isAuthenticated, user?.sub]);

  useEffect(() => {
    let mounted = true;
    
    const checkRegistrationStatuses = () => {
      if (!futureSessionGroups || futureSessionGroups.length === 0) return;
      
      const registrationStatuses = {};
      if (sessionGroup?.id && isRegistered) {
        registrationStatuses[sessionGroup.id] = true;
      }
      
      futureSessionGroups.forEach(group => {
        const storedRegistration = localStorage.getItem(`edu-workshop-registered-${group.id}`);
        if (storedRegistration) {
          try {
            const registrationData = JSON.parse(storedRegistration);
            // CRITICAL FIX: For workshop, allow both authenticated users with matching ID and unauthenticated guest registrations
            if ((!isAuthenticated && !registrationData.userId) || 
                (isAuthenticated && registrationData.userId === user?.sub)) {
              registrationStatuses[group.id] = true;
            } else if (isAuthenticated && registrationData.userId && registrationData.userId !== user?.sub) {
              // Clear registration data from a different user
              console.log(`[WeeklyWorkshop] Clearing registration data from different user: ${registrationData.userId} vs ${user?.sub}`);
              localStorage.removeItem(`edu-workshop-registered-${group.id}`);
            }
          } catch (err) {
            console.error(`Error parsing stored registration data for cohort ${group.id}:`, err);
            localStorage.removeItem(`edu-workshop-registered-${group.id}`);
          }
        } else {
          if (group.isRegistered) {
            registrationStatuses[group.id] = true;
          }
        }
      });
      
      setRegisteredCohorts(registrationStatuses);
    };
    
    checkRegistrationStatuses();
    
    return () => {
      mounted = false;
    };
  }, [futureSessionGroups, sessionGroup, isRegistered, isAuthenticated, user]);

  useEffect(() => {
    // Auto scroll to past recordings if query param provided
    const params = new URLSearchParams(window.location.search);
    if (params.get('scroll_to') === 'previously_recorded') {
      // Delay until pastSessionGroups rendered
      const timer = setTimeout(() => {
        const el = document.getElementById('previously-recorded');
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.scrollBy({ top: -80, left: 0, behavior: 'smooth' });
        }
      }, 200); // small delay to ensure DOM ready
      return () => clearTimeout(timer);
    }
  }, [pastSessionGroups]);

  useEffect(() => {
    // Handle anchor links for session groups and individual sessions (from email notifications)
    const hash = window.location.hash;
    
    if (hash && (hash.startsWith('#session-group-') || hash.startsWith('#session-')) && !isScrollingToSessionRef.current) {
      console.log('[SESSION-SCROLL] 🎯 EMAIL LINK DETECTED:', hash);
      console.log('[SESSION-SCROLL] Target type:', hash.startsWith('#session-group-') ? 'SESSION GROUP' : 'INDIVIDUAL SESSION');
      
      isScrollingToSessionRef.current = true; // Lock to prevent conflicts
      
      // Function to attempt scroll with retries
      const attemptScroll = (retryCount = 0, maxRetries = 5) => {
        console.log(`[SESSION-SCROLL] 🔍 Scroll attempt ${retryCount + 1}/${maxRetries + 1} for:`, hash);
        const element = document.querySelector(hash);
        
        if (element) {
          console.log('[SESSION-SCROLL] ✅ ELEMENT FOUND - Starting scroll to:', hash);
          
          // Prevent any competing scroll behavior
          window.history.replaceState(null, null, window.location.pathname + window.location.search + hash);
          
          // Use a more forceful scroll approach
          const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
          const headerOffset = 100; // Extra offset for header and spacing
          const targetPosition = elementTop - headerOffset;
          
          console.log('[SESSION-SCROLL] Scroll details:', {
            elementTop: elementTop,
            headerOffset: headerOffset,
            targetPosition: targetPosition,
            currentScroll: window.pageYOffset
          });
          
          // Smooth scroll to position
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
          
          // Verify scroll completed after animation
          setTimeout(() => {
            const finalPosition = window.pageYOffset;
            console.log('[SESSION-SCROLL] ✅ SCROLL COMPLETE. Final position:', finalPosition, 'Target was:', targetPosition);
            
            // If we're not close enough to target, try a direct approach
            if (Math.abs(finalPosition - targetPosition) > 50) {
              console.log('[SESSION-SCROLL] 🔧 Position not accurate, using direct scroll');
              element.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start',
                inline: 'nearest'
              });
              setTimeout(() => {
                window.scrollBy({ top: -80, left: 0, behavior: 'smooth' });
                isScrollingToSessionRef.current = false; // Unlock after final adjustment
              }, 200);
            } else {
              isScrollingToSessionRef.current = false; // Unlock after successful scroll
            }
          }, 1000);
          
        } else if (retryCount < maxRetries) {
          console.log(`[SESSION-SCROLL] ❌ ELEMENT NOT FOUND (attempt ${retryCount + 1}) - Retrying in ${(retryCount + 1) * 500}ms...`);
          setTimeout(() => attemptScroll(retryCount + 1, maxRetries), (retryCount + 1) * 500);
        } else {
          console.log('[SESSION-SCROLL] ❌ FINAL RETRY FAILED - Element not found after all attempts:', hash);
          isScrollingToSessionRef.current = false; // Unlock even on failure
        }
      };
      
      // Start scroll attempts after initial delay
      const timer = setTimeout(() => attemptScroll(), 600);
      return () => {
        clearTimeout(timer);
        isScrollingToSessionRef.current = false; // Cleanup
      };
    }
  }, [pastSessionGroups]);

  // Handle hash changes for users clicking session links while already on the page
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash && (hash.startsWith('#session-group-') || hash.startsWith('#session-')) && !isScrollingToSessionRef.current) {
        console.log('[SESSION-SCROLL] 🔄 HASH CHANGED TO:', hash);
        
        // Allow a brief moment for any DOM updates, then scroll
        setTimeout(() => {
          const element = document.querySelector(hash);
          if (element) {
            console.log('[SESSION-SCROLL] 🎯 SCROLLING TO HASH CHANGE TARGET:', hash);
            element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
            // Small offset adjustment for header
            setTimeout(() => {
              window.scrollBy({ top: -80, left: 0, behavior: 'smooth' });
            }, 300);
          }
        }, 100);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  const formatWorkshopDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      
      const timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timeZoneAbbr = new Intl.DateTimeFormat('en', {
        timeZoneName: 'short',
        timeZone: timeZoneName
      }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || '';
      
      return format(date, "EEEE, MMMM do 'at' h:mm a") + " " + timeZoneAbbr;
    } catch (error) {
      return "Date unavailable";
    }
  };

  const validateForm = () => {
    const errors = { name: '', email: '', phone: '' };
    let isValid = true;
    if (!guestInfo.name || guestInfo.name.trim() === '') {
      errors.name = 'Name is required';
      isValid = false;
    } else if (guestInfo.name.trim().length < 2) {
      errors.name = 'Please enter your full name';
      isValid = false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!guestInfo.email || guestInfo.email.trim() === '') {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!emailRegex.test(guestInfo.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }
    if (guestInfo.phone && guestInfo.phone.trim() !== '') {
      const phoneDigits = guestInfo.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        errors.phone = 'Please enter a valid phone number';
        isValid = false;
      }
    }
    setFormErrors(errors);
    return isValid;
  };

  const handleRegistration = async (extra = {}) => {
    if (!sessionGroup?.id) return;
    setApiError('');
    if (!isAuthenticated && !validateForm()) return;
    setIsRegistering(true);
    try {
      const registrationData = isAuthenticated
        ? { email: user.email }
        : {
            email: guestInfo.email,
            firstName: guestInfo.name.split(' ')[0],
            lastName: guestInfo.name.split(' ').slice(1).join(' '),
            phone: guestInfo.phone,
            ...(extra.smsConsent ? { smsConsent: extra.smsConsent } : {})
          };
      let headers = { 'Content-Type': 'application/json' };
      if (isAuthenticated) {
        const token = await getApiToken();
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/courses/session-groups/${sessionGroup.id}/register`,
        { method: 'POST', headers, body: JSON.stringify(registrationData) }
      );
      if (!response.ok) {
        let errorMessage = `Registration failed: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {}
        throw new Error(errorMessage);
      }
      const storageData = {
        sessionGroupId: sessionGroup.id,
        timestamp: new Date().toISOString(),
        userId: isAuthenticated ? user?.sub : null,
        email: isAuthenticated ? user?.email : guestInfo.email
      };
      localStorage.setItem(
        `edu-workshop-registered-${sessionGroup.id}`,
        JSON.stringify(storageData)
      );
      setIsRegistered(true);

      // --- Analytics: Register ---
      pushEvent({
        event: ANALYTICS_EVENTS.COURSE_REGISTER,
        courseType: 'workshop',
        groupId: sessionGroup.id,
        isAuthenticated,
        email: isAuthenticated ? user?.email : guestInfo.email
      });

      if (isAuthenticated) {
        const token = await getApiToken();
        const refreshResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/courses/session-groups/${sessionGroup.id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (refreshResponse.ok) {
          const updatedGroup = await refreshResponse.json();
          setSessionGroup(updatedGroup);
        }
      }
    } catch (err) {
      setApiError(err.message || 'Registration failed. Please try again later.');
    } finally {
      setIsRegistering(false);
    }
  };

  const handleUpcomingCohortRegistration = async (cohortId, extra = {}) => {
    if (!cohortId) return;
    setApiError('');
    if (!isAuthenticated && !validateForm()) return;
    setIsRegistering(true);
    try {
      const registrationData = isAuthenticated
        ? { email: user.email }
        : {
            email: guestInfo.email,
            firstName: guestInfo.name.split(' ')[0],
            lastName: guestInfo.name.split(' ').slice(1).join(' '),
            phone: guestInfo.phone,
            ...(extra.smsConsent ? { smsConsent: extra.smsConsent } : {})
          };
      let headers = { 'Content-Type': 'application/json' };
      if (isAuthenticated) {
        const token = await getApiToken();
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/courses/session-groups/${cohortId}/register`,
        { method: 'POST', headers, body: JSON.stringify(registrationData) }
      );
      if (response.status === 409) {
        setRegisteredCohorts(prev => ({ ...prev, [cohortId]: true }));
        const storageData = {
          sessionGroupId: cohortId,
          timestamp: new Date().toISOString(),
          userId: isAuthenticated ? user?.sub : null,
          email: isAuthenticated ? user?.email : guestInfo.email
        };
        localStorage.setItem(
          `edu-workshop-registered-${cohortId}`,
          JSON.stringify(storageData)
        );
        setApiError("You're already registered for this cohort!");
        return;
      }
      if (!response.ok) {
        let errorMessage = `Registration failed: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {}
        throw new Error(errorMessage);
      }
      const storageData = {
        sessionGroupId: cohortId,
        timestamp: new Date().toISOString(),
        userId: isAuthenticated ? user?.sub : null,
        email: isAuthenticated ? user?.email : guestInfo.email
      };
      localStorage.setItem(
        `edu-workshop-registered-${cohortId}`,
        JSON.stringify(storageData)
      );
      setRegisteredCohorts(prev => ({ ...prev, [cohortId]: true }));
    } catch (err) {
      setApiError(err.message || 'Registration failed. Please try again later.');
    } finally {
      setIsRegistering(false);
    }
  };

  const handleUnregister = async () => {
    if (!sessionGroup?.id || !isAuthenticated) return;
    try {
      const token = await getApiToken();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/courses/session-groups/${sessionGroup.id}/unregister`,
        { method: 'DELETE', headers }
      );
      if (!response.ok) throw new Error(`Unregistration failed: ${response.status}`);
      localStorage.removeItem(`edu-workshop-registered-${sessionGroup.id}`);
      setIsRegistered(false);

      // --- Analytics: Unregister ---
      pushEvent({
        event: ANALYTICS_EVENTS.COURSE_UNREGISTER,
        courseType: 'workshop',
        groupId: sessionGroup.id,
        isAuthenticated: true,
        email: user?.email
      });
    } catch (err) {
      // Optionally show error
    }
  };

  const handleUpcomingCohortUnregister = async (cohortId) => {
    if (!cohortId || !isAuthenticated) return;
    try {
      const token = await getApiToken();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/courses/session-groups/${cohortId}/unregister`,
        { method: 'DELETE', headers }
      );
      if (!response.ok) throw new Error(`Unregistration failed: ${response.status}`);
      localStorage.removeItem(`edu-workshop-registered-${cohortId}`);
      setRegisteredCohorts(prev => {
        const updated = { ...prev };
        delete updated[cohortId];
        return updated;
      });
    } catch (err) {
      // Optionally show error
    }
  };

  if (authLoading || isLoading) {
    return <Loading />;
  }

  console.log('Session group info:', {
    hasSessionGroup: !!sessionGroup,
    name: sessionGroup?.name,
    isRegistered: isRegistered,
    sessionCount: sessionGroup?.sessions?.length || 0
  });

  return (
    <div className="edu-workshop-container">
      <Container>
        <header className="edu-workshop-header">
          {courseData ? (
            <>
              {courseData.title && (
                <h1 className="edu-workshop-title">{courseData.title}</h1>
              )}
              {courseData.description && (
                <div
                  className="edu-workshop-subtitle"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(courseData.description) }}
                />
              )}
              {courseData.longDescription && (
                <div
                  className="edu-workshop-long-description"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(courseData.longDescription) }}
                />
              )}
              
              {/* Workshop Benefits */}
              <div className="workshop-benefits">
                <h4>🔥 What You'll See Live:</h4>
                <ul>
                  <li>A brand-new AI trading bot built from scratch in front of you</li>
                  <li>Real money deployed on live trades — no demos, no do-overs</li>
                  <li>Backtests and live results revealed instantly as they happen</li>
                  <li>Wins and losses shown transparently, with nothing hidden</li>
                  <li>Audience ideas tested live and added to the marketplace</li>
                  <li>Every bot created goes public on BotSpot Marketplace for you to explore and use</li>
                </ul>
              </div>
            </>
          ) : !serviceUnavailable ? (
            <div className="edu-workshop-loading">
              <Spinner animation="border" role="status" className="mb-3" />
              <p>Loading workshop information...</p>
            </div>
          ) : null}
        </header>

        {serviceUnavailable && (
          <div className="edu-workshop-info-message">
            <div className="edu-info-card">
              <div className="edu-info-icon">
                <i className="fas fa-calendar-alt"></i>
              </div>
              <h3>Workshop Information</h3>
              <p>We're currently updating our workshop schedule. Please check back soon for upcoming sessions.</p>
              <Button 
                variant="primary"
                onClick={handleRefresh}
                className="edu-refresh-button"
              >
                <i className="fas fa-sync-alt me-2"></i>
                Refresh
              </Button>
            </div>
          </div>
        )}

        {!serviceUnavailable && (
          <>
            {sessionGroup && (
              <section className="edu-workshop-content">
                <SessionGroupCard
                  group={{
                    ...sessionGroup,
                    description: sessionGroup?.description
                      ? DOMPurify.sanitize(sessionGroup.description)
                      : undefined,
                    longDescription: sessionGroup?.longDescription
                      ? DOMPurify.sanitize(sessionGroup.longDescription)
                      : undefined,
                    sessions: sessionGroup?.sessions?.map(session => ({
                      ...session,
                      description: session.description
                        ? DOMPurify.sanitize(session.description)
                        : undefined,
                      longDescription: session.longDescription
                        ? DOMPurify.sanitize(session.longDescription)
                        : undefined,
                    })),
                  }}
                  isCurrent={true}
                  isRegistered={isRegistered}
                  isRegistering={isRegistering}
                  isAuthenticated={isAuthenticated}
                  user={user}
                  apiError={apiError}
                  formErrors={formErrors}
                  guestInfo={guestInfo}
                  setGuestInfo={setGuestInfo}
                  handleRegistration={handleRegistration}
                  handleUnregister={handleUnregister}
                  handleUpcomingCohortRegistration={handleUpcomingCohortRegistration}
                  handleUpcomingCohortUnregister={handleUpcomingCohortUnregister}
                  formatWorkshopDate={formatWorkshopDate}
                  courseUserHasAccess={courseData?.userHasAccess}
                  courseIsPublic={true}
                  navigate={navigate}
                  loginWithRedirect={loginWithRedirect}
                />
              </section>
            )}

            {futureSessionGroups && futureSessionGroups.length > 0 && (
              <section className="edu-future-workshops">
                <h3 className="edu-section-title">Upcoming</h3>
                {futureSessionGroups.slice(0, upcomingVisibleCount).map(group => (
                  <SessionGroupCard
                    key={group.id}
                    group={{
                      ...group,
                      description: group?.description
                        ? DOMPurify.sanitize(group.description)
                        : undefined,
                      longDescription: group?.longDescription
                        ? DOMPurify.sanitize(group.longDescription)
                        : undefined,
                      sessions: group?.sessions?.map(session => ({
                        ...session,
                        description: session.description
                          ? DOMPurify.sanitize(session.description)
                          : undefined,
                        longDescription: session.longDescription
                          ? DOMPurify.sanitize(session.longDescription)
                          : undefined,
                      })),
                    }}
                    isCurrent={false}
                    isRegistered={registeredCohorts[group.id] || group.isRegistered}
                    isRegistering={isRegistering}
                    isAuthenticated={isAuthenticated}
                    user={user}
                    apiError={apiError}
                    formErrors={formErrors}
                    guestInfo={guestInfo}
                    setGuestInfo={setGuestInfo}
                    handleRegistration={handleRegistration}
                    handleUnregister={handleUnregister}
                    handleUpcomingCohortRegistration={handleUpcomingCohortRegistration}
                    handleUpcomingCohortUnregister={handleUpcomingCohortUnregister}
                    formatWorkshopDate={formatWorkshopDate}
                    courseUserHasAccess={courseData?.userHasAccess}
                    courseIsPublic={true}
                    navigate={navigate}
                    loginWithRedirect={loginWithRedirect}
                  />
                ))}
                {upcomingVisibleCount < futureSessionGroups.length && (
                  <div style={{ textAlign: 'center', marginTop: '0.5rem', marginBottom: '2.5rem' }}>
                    <Button
                      variant="secondary"
                      className="edu-upcoming-load-more-btn"
                      style={{
                        minWidth: 200,
                        fontWeight: 600,
                        fontSize: '1.08rem',
                        borderRadius: 24,
                        background: 'var(--education-surface, #444)',
                        border: 'none',
                        color: 'var(--education-ink, #fff)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        transition: 'background 0.2s',
                      }}
                      onClick={() => setUpcomingVisibleCount(prev => Math.min(prev + 1, futureSessionGroups.length))}
                    >
                      Load more upcoming sessions
                    </Button>
                  </div>
                )}
              </section>
            )}

            {pastSessionGroups.length > 0 && (
              <PastSessionGroups
                pastSessionGroups={pastSessionGroups.map(group => ({
                  ...group,
                  description: group?.description
                    ? DOMPurify.sanitize(group.description)
                    : undefined,
                  longDescription: group?.longDescription
                    ? DOMPurify.sanitize(group.longDescription)
                    : undefined,
                  sessions: group?.sessions?.map(session => ({
                    ...session,
                    description: session.description
                      ? DOMPurify.sanitize(session.description)
                      : undefined,
                    longDescription: session.longDescription
                      ? DOMPurify.sanitize(session.longDescription)
                      : undefined,
                  })),
                }))}
                formatWorkshopDate={formatWorkshopDate}
                isAuthenticated={isAuthenticated}
                courseRequiresPro={false}
                userHasProSubscription={false}
                navigate={navigate}
                loginWithRedirect={loginWithRedirect}
              />
            )}

            {courseData && !sessionGroup && futureSessionGroups.length === 0 && pastSessionGroups.length === 0 && !serviceUnavailable && (
              <div className="edu-no-sessions-message">
                <Card className="edu-info-card">
                  <Card.Body className="text-center py-4">
                    <i className="fas fa-calendar-alt edu-info-icon mb-3"></i>
                    <h3>Workshop Cohorts Coming Soon</h3>
                    <p>We're preparing new workshop cohorts. Check back soon for updates.</p>
                  </Card.Body>
                </Card>
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default WeeklyWorkshop;
